import { Q } from '@nozbe/watermelondb'
import Participant from 'model/models/Participant'
import {
  DocType,
  GroupDoc,
  GroupStatus,
  UserSettingsDoc
} from 'types/api/changes'
import { CollectionType, Model } from 'types/model'

import Database from '../Database'
import { is24meApp, isGroupcalApp } from 'utils/appType'
import { t } from 'i18next'
import { isConvertedGroup } from 'utils/groups'
import { outlookPrefix, prefixGoogleEventsGroup } from 'utils/constants'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { UserSettings } from './UserSettingsQueries'
import {
  getArchivedGroups,
  getDeletedGroups,
  isGroupDeleted
} from 'utils/api/userSettings'
import { arch } from 'os'
import { RemoveThirdPartyEvents } from './Event'
import { isTrulyEmpty } from 'utils/object'

export enum GroupFilter {
  LastUpdated = 'lastUpdated',
  NextEvent = 'nextEvent',
  Unread = 'unread',
  Archived = 'archived'
}

export const tfmGoogleTempLabel = 'tempgroup'

export const AddTempGroup = async () => {
  return await Database.getCollection<GroupDoc>(CollectionType.GROUP).create(
    (group) => {
      group.Name = 'New Shared Calendar'
      group.Type = DocType.GROUP
      group.OwnerID = LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      group.GroupSettings = {
        IsAllParticipantsCanAddItems: '1',
        IsAllParticipantsCanAddParticipants: '1',
        IsAllParticipantsCanEditGroupMetadata: '1',
        IsAllParticipantsCanEditOnlyTheirEvents: '0',
        IsAllParticipantsCanSeeFreeBusyOnly: '0'
      }
      group.OwnerID = LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      group._raw['_sortOrder'] = Date.now() / 1000
      group._raw['LastUpdateMessage'] = {
        type: t('youCreatedCalendar')
      }
      group._raw['_rev'] = 'temp'
    }
  )
}

export const RemoveTempGroups = () => {
  Database.write(async () => {
    const groupsToRemove = await Database.getCollection<GroupDoc>(
      CollectionType.GROUP
    )
      .query(Q.or(Q.where('_rev', 'temp'), Q.where('_id', '')))
      .fetch()

    groupsToRemove.map(async (group) => {
      await group.destroyPermanently()
    })
  })
}

export const ParticipantsAll = () => {
  return Database.getCollection<Participant>(CollectionType.PARTICIPANT).query()
}

export const ParticipantsForGroup = (participantIds: string[]) => {
  return Database.getCollection<Participant>(CollectionType.PARTICIPANT).query(
    Q.where('AccountID', Q.oneOf(participantIds))
  )
}

export async function provideFirstGroup() {
  let query: any[] = [Q.sortBy('_sortOrder', Q.desc)]
  if (isGroupcalApp())
    query.push(Q.where('_id', Q.notLike(`%${prefixGoogleEventsGroup}%`)))
  const groups = await Database.getCollection<GroupDoc>(CollectionType.GROUP)
    .query(...query)
    .fetch()

  if (!groups || groups.length === 0) return undefined

  return groups[0]
}

export async function allGroups() {
  const groups = await Database.getCollection<GroupDoc>(CollectionType.GROUP)
    .query()
    .fetch()
  return groups.sort((a, b) => b._sortOrder - a._sortOrder)
}

export function allGroupsForUI(
  groups: Model<GroupDoc>[] | GroupDoc[],
  userSettings: UserSettingsDoc | undefined,
  withArchived = false,
  filter: string = GroupFilter.LastUpdated
) {
  const groupsForUI = groups
    .filter((group) => {
      return isGroupDeleted(userSettings, group._id ?? '') === false
    })
    .filter((group) => {
      if (withArchived) return true

      return (
        userSettings?.GroupsSettings?.[group._id ?? '']?.GroupStatus !==
        GroupStatus.ARCHIVED
      )
    })

  if (groupsForUI.length === 0 && is24meApp()) {
    const tempGroupForUI: GroupDoc = {
      CreationDate: '',
      GroupColor: '',
      GroupSettings: {
        IsAllParticipantsCanAddItems: '',
        IsAllParticipantsCanAddParticipants: '',
        IsAllParticipantsCanEditGroupMetadata: '',
        IsAllParticipantsCanEditOnlyTheirEvents: '0',
        IsAllParticipantsCanSeeFreeBusyOnly: '0'
      },
      Name: t('personalCalendar'),
      OwnerID: '',
      Participants: {},
      PendingParticipants: {},
      Photo: '',
      PrivacyMode: '',
      PrivateLinkPassword: '',
      PrivateLinkUrl: '',
      Status: '',
      Version: '',
      VerifiedGroupStatus: '',
      SyncProblem: undefined,
      _message: t('clickToAddYour'),
      _sortOrder: 0,
      _id: `tempid`,
      _rev: 'temprev',
      BusinessInfo: undefined,
      LastUpdateEvent: undefined,
      Events: undefined,
      CalendarEmail: undefined,
      CalendarType: undefined,
      PersonalGroupSyncStatus: undefined
    }

    return [tempGroupForUI]
  }

  if (filter !== GroupFilter.LastUpdated) {
    if (filter === GroupFilter.Archived) {
      return groupsForUI.filter((group) => {
        return (
          userSettings?.GroupsSettings?.[group._id ?? '']?.GroupStatus ===
          GroupStatus.ARCHIVED
        )
      })
    } else if (filter === GroupFilter.Unread) {
      return groupsForUI.filter((group) => {
        return (
          userSettings?.GroupsSettings?.[group._id ?? '']?.Unread === '1' ||
          Object.keys(
            userSettings?.GroupsSettings?.[group._id ?? '']?.UnreadItems ?? {}
          ).length > 0
        )
      })
    }
  }

  return groupsForUI
}

export async function thirdPartyProviderGroup(id: string) {
  return (await allGroups()).find(
    (group) => group._id.includes(id) || group.CalendarEmail?.includes(id)
  )
}

export async function allOutlookCalendars() {
  return (await allGroups()).filter((group) =>
    group.CalendarEmail?.includes(outlookPrefix)
  )
}

export async function ClearThirdPartyData(id: string) {
  return RemoveThirdPartyEvents(id).then(() => {
    return RemoveThirdPartyCalendar(id)
  })
}

export async function RemoveThirdPartyCalendar(id: string) {
  return Database.write(async () => {
    const groups = (await allGroups())
      .filter(
        (group) =>
          (group.CalendarEmail?.includes(id) || group._id.includes(id)) &&
          isTrulyEmpty(group._rev)
      )
      .map((group) => {
        if (isTrulyEmpty(group._rev)) {
          group.destroyPermanently()
        }
      })
    return Promise.all(groups)
  })
}

export async function localGroupById(id: string) {
  return (
    await Database.getCollection<GroupDoc>(CollectionType.GROUP)
      .query(Q.where('_id', id))
      .fetch()
  )[0]
}

export async function setGroupSortOrder(id: string, sortOrder: number) {
  return await Database.write(async () => {
    const group = await localGroupById(id)

    return await group.update(() => {
      group._raw['_sortOrder'] = sortOrder
    })
  })
}
